import { Controller } from "@hotwired/stimulus"

const FIELDS = ["provincia", "localidad"]
const MESSAGES = {
  provincia: "Seleccione una provincia de la lista o borre el texto (el campo es opcional).",
  localidad: "Seleccione una localidad o departamento de la lista o borre el texto (el campo es opcional)."
}
const TIPO_DEPARTAMENTO = "departamento"

const normalize = (value) =>
  (value || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim()

// Connects to data-controller="georef-location-selector"

export default class extends Controller {
  static targets = [
    "provinciaInput", "provinciaId", "provinciaNombre", "provinciaFeedback",
    "localidadInput", "localidadSeleccion", "localidadId", "localidadNombre",
    "departamentoId", "departamentoNombre", "localidadFeedback", "localidadAutocomplete"
  ]

  static values = { localidadesUrl: String }

  connect() {
    this.provinciaSeleccionada = this.provinciaIdTarget.value
    this.updateLocalidadAvailability()
  }

  disconnect() {
    FIELDS.forEach((field) => clearTimeout(this[`${field}BlurTimeout`]))
  }

  provinciaInput() {
    this.provinciaIdTarget.value = ""
    this.provinciaNombreTarget.value = ""
    this.clearError("provincia")

    if (!this.provinciaInputTarget.value.trim()) {
      this.provinciaSeleccionada = ""
      this.clearLocalidad()
    }

    this.updateLocalidadAvailability()
  }

  provinciaChanged() {
    const provinciaId = this.provinciaIdTarget.value
    if (!provinciaId) return

    this.provinciaNombreTarget.value = this.provinciaInputTarget.value.trim()
    this.clearError("provincia")

    const cambio = provinciaId !== this.provinciaSeleccionada
    this.provinciaSeleccionada = provinciaId
    if (cambio) this.clearLocalidad()

    this.updateLocalidadAvailability()
    if (cambio) requestAnimationFrame(() => this.localidadInputTarget.focus())
  }

  localidadInput() {
    this.clearUbicacion()
    this.clearError("localidad")
  }

  localidadChanged() {
    const value = this.localidadSeleccionTarget.value
    if (!value) return

    const option = this.optionsFor("localidad")
      .find((element) => element.dataset.autocompleteValue === value)
    if (option) this.applyLocalidad(option)
  }

  provinciaBlur() {
    this.scheduleCheck("provincia")
  }

  localidadBlur() {
    this.scheduleCheck("localidad")
  }

  validate(event) {
    const pendientes = FIELDS.filter((field) => this.isPending(field))
    if (pendientes.length === 0) return

    event.preventDefault()
    pendientes.forEach((field) => this.showError(field))
    this.inputFor(pendientes[0]).focus()
  }

  // private

  scheduleCheck(field) {
    clearTimeout(this[`${field}BlurTimeout`])
    this[`${field}BlurTimeout`] = setTimeout(() => {
      if (this.isPending(field)) this.showError(field)
    }, 250)
  }

  isPending(field) {
    const input = this.inputFor(field)
    if (input.disabled || !input.value.trim()) return false
    if (this.hasSelection(field)) return false

    return !this.selectExactMatch(field)
  }

  hasSelection(field) {
    if (field === "provincia") return !!this.provinciaIdTarget.value

    return !!(this.localidadIdTarget.value || this.departamentoIdTarget.value)
  }

  selectExactMatch(field) {
    const input = this.inputFor(field)
    const texto = normalize(input.value)
    const match = this.optionsFor(field).find((option) =>
      [option.dataset.georefNombre, option.dataset.autocompleteLabel, option.textContent]
        .some((candidato) => candidato && normalize(candidato) === texto)
    )
    if (!match) return false

    input.value = match.dataset.autocompleteLabel || match.textContent.trim()
    if (field === "provincia") {
      this.provinciaIdTarget.value = match.dataset.autocompleteValue
      this.provinciaIdTarget.dispatchEvent(new Event("change"))
    } else {
      this.localidadSeleccionTarget.value = match.dataset.autocompleteValue
      this.applyLocalidad(match)
    }
    return true
  }

  optionsFor(field) {
    return Array.from(
      this.inputFor(field).closest("[data-controller~='georef-autocomplete']")
        .querySelectorAll("[role='option'][data-autocomplete-value]:not([aria-disabled])")
    )
  }

  applyLocalidad(option) {
    const { autocompleteValue, georefNombre, georefTipo, georefDepartamentoId, georefDepartamentoNombre } = option.dataset

    if (georefTipo === TIPO_DEPARTAMENTO) {
      this.localidadIdTarget.value = ""
      this.localidadNombreTarget.value = ""
      this.departamentoIdTarget.value = autocompleteValue
      this.departamentoNombreTarget.value = georefNombre || ""
    } else {
      this.localidadIdTarget.value = autocompleteValue
      this.localidadNombreTarget.value = georefNombre || this.localidadInputTarget.value.trim()
      this.departamentoIdTarget.value = georefDepartamentoId || ""
      this.departamentoNombreTarget.value = georefDepartamentoNombre || ""
    }

    this.clearError("localidad")
  }

  clearUbicacion() {
    this.localidadSeleccionTarget.value = ""
    this.localidadIdTarget.value = ""
    this.localidadNombreTarget.value = ""
    this.departamentoIdTarget.value = ""
    this.departamentoNombreTarget.value = ""
  }

  clearLocalidad() {
    this.localidadInputTarget.value = ""
    this.clearUbicacion()
    this.clearError("localidad")
  }

  updateLocalidadAvailability() {
    const provinciaId = this.provinciaIdTarget.value
    const input = this.localidadInputTarget

    input.disabled = !provinciaId
    input.placeholder = provinciaId ? "Buscar localidad o departamento..." : "Primero seleccione una provincia"
    if (!provinciaId) this.clearError("localidad")

    this.localidadAutocompleteTarget.dataset.georefAutocompleteUrlValue = this.localidadesUrlFor(provinciaId)
    this.localidadAutocompleteTarget.dispatchEvent(new CustomEvent("georef-autocomplete:invalidate"))
  }

  localidadesUrlFor(provinciaId) {
    if (!provinciaId) return this.localidadesUrlValue

    return `${this.localidadesUrlValue}?provincia_id=${encodeURIComponent(provinciaId)}`
  }

  showError(field) {
    this.inputFor(field).classList.add("is-invalid")
    const feedback = this[`${field}FeedbackTarget`]
    feedback.textContent = MESSAGES[field]
    feedback.classList.add("d-block")
  }

  clearError(field) {
    clearTimeout(this[`${field}BlurTimeout`])
    this.inputFor(field).classList.remove("is-invalid")
    const feedback = this[`${field}FeedbackTarget`]
    feedback.textContent = ""
    feedback.classList.remove("d-block")
  }

  inputFor(field) {
    return this[`${field}InputTarget`]
  }
}
